import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, LogOut, Radio, Copy, Map as MapIcon, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, AlertTriangle, ArrowRight, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import MapView from "@/components/MapView";
import CreateDeliveryForm from "@/components/CreateDeliveryForm";
import SearchBookingsDialog from "@/components/SearchBookingsDialog";
import TripDetailsDialog from "@/components/TripDetailsDialog";
import BrandLogo from "@/components/BrandLogo";

const cls = "h-11 bg-white border-2 font-mono text-sm";

function Badge({ status }) {
  const m = { pending:"warning", assigned:"blue-400", picked_up:"purple-400", in_transit:"brand-green", delivered:"brand-success", cancelled:"brand-danger" };
  return <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-${m[status]||"foreground"} text-${m[status]||"foreground"}`}>{status}</span>;
}

export default function OperationsDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("deliveries");
  const [deliveries, setDeliveries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [assignFor, setAssignFor] = useState(null);
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(true);
  const [reordersOpen, setReordersOpen] = useState(true);
  const [bookingsOpen, setBookingsOpen] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [tripDetail, setTripDetail] = useState(null);
  const [magicLinkResult, setMagicLinkResult] = useState(null);
  const [reorders, setReorders] = useState([]);
  const [reorderDialog, setReorderDialog] = useState(null);

  const isAdmin = user.role === "admin";

  const load = useCallback(async () => {
    try {
      const [dRes, aRes, drRes, cRes, sRes, roRes] = await Promise.all([
        api.get("/deliveries"), api.get("/accounts"), api.get("/drivers"),
        api.get("/customers"), api.get("/ops/stats"), api.get("/ops/pending-reorders"),
      ]);
      setDeliveries(dRes.data.deliveries); setAccounts(aRes.data.accounts);
      setDrivers(drRes.data.drivers); setCustomers(cRes.data.customers); setStats(sRes.data);
      setReorders(roRes.data.reorders);
      if (isAdmin) {
        const u = await api.get("/users");
        setUsers(u.data.users);
      }
    } catch (err) { toast.error(formatApiError(err)); }
  }, [isAdmin]);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const assign = async (deliveryId, driverId) => {
    try { await api.post(`/deliveries/${deliveryId}/assign`, { driver_id: driverId }); toast.success("Assigned"); setAssignFor(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const resolveReorder = async (reqId, action) => {
    try {
      await api.post(`/ops/reorders/${reqId}/${action}`);
      toast.success(action === "approve" ? "Route change approved" : "Route change rejected");
      setReorderDialog(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // delivery_id -> reorder request (only approval-needed 'pending' reorders flash the card)
  const reorderByDelivery = {};
  reorders.filter((r) => r.status === "pending").forEach((r) => (r.proposed_order || []).forEach((o) => { reorderByDelivery[o.delivery_id] = r; }));

  const driverMarkers = drivers.filter(d=>d.current_lat != null && d.online).map(d => ({ type:"driver", lat:d.current_lat, lng:d.current_lng, title:`${d.driver_number||""} ${d.name}`, label:d.vehicle_type||d.vehicle||d.phone }));
  const driverFit = driverMarkers.map(m => [m.lat, m.lng]);
  const driverFitKey = driverMarkers.map(m => `${m.lat.toFixed(4)},${m.lng.toFixed(4)}`).join("|");

  const STATUS_META = {
    pending: { label: "pending", cls: "text-brand-warning" },
    assigned: { label: "assigned", cls: "text-muted-foreground" },
    picked_up: { label: "picked up", cls: "text-muted-foreground" },
    in_transit: { label: "in transit", cls: "text-brand-green" },
    delivered: { label: "delivered", cls: "text-brand-success" },
    cancelled: { label: "cancelled", cls: "text-brand-danger" },
  };
  const statusCounts = Object.keys(STATUS_META)
    .map((s) => ({ s, n: deliveries.filter((d) => d.status === s).length }))
    .filter((x) => x.n > 0);
  const visibleDeliveries = statusFilter ? deliveries.filter((d) => d.status === statusFilter) : deliveries;
  const pickStatus = (s) => { setStatusFilter((cur) => (cur === s ? null : s)); setBookingsOpen(true); };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between bg-card">
        <div className="flex items-center gap-4">
          <BrandLogo className="h-9" />
          <div>
            <div className="font-display text-lg uppercase tracking-tight">COUNTRY COURIER // OPS</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{user.role} · {user.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="font-mono text-[10px] uppercase text-brand-success flex items-center gap-2"><Radio className="w-3 h-3 animate-pulse"/>LIVE</div>
          <Button variant="outline" onClick={()=>{logout();navigate("/");}} className="font-mono uppercase text-xs" data-testid="logout-btn"><LogOut className="w-3 h-3 mr-2"/>Sign Out</Button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border">
        {[["Total",stats.total||0],["Pending",stats.pending||0],["In Transit",stats.in_transit||0],["Delivered",stats.delivered||0],[`Drivers (${stats.online_drivers||0}/${stats.drivers||0})`,stats.drivers||0],["Accounts",stats.accounts||0]].map(([l,v])=>(
          <div key={l} className="bg-card p-4"><div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div><div className="text-3xl font-display">{v}</div></div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col">
        <TabsList className={`grid ${isAdmin?"grid-cols-5":"grid-cols-4"} rounded-none bg-muted/50 h-12`}>
          <TabsTrigger value="deliveries" data-testid="tab-deliveries" className="font-mono uppercase text-xs tracking-widest data-[state=active]:bg-brand-green data-[state=active]:text-black">Deliveries</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts" className="font-mono uppercase text-xs tracking-widest data-[state=active]:bg-brand-green data-[state=active]:text-black">Accounts</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers" className="font-mono uppercase text-xs tracking-widest data-[state=active]:bg-brand-green data-[state=active]:text-black">Customers</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="tab-drivers" className="font-mono uppercase text-xs tracking-widest data-[state=active]:bg-brand-green data-[state=active]:text-black">Drivers</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users" className="font-mono uppercase text-xs tracking-widest data-[state=active]:bg-brand-green data-[state=active]:text-black">Users</TabsTrigger>}
        </TabsList>

        <TabsContent value="deliveries" className="data-[state=active]:grid lg:grid-cols-12 lg:auto-rows-[minmax(0,1fr)] gap-px bg-border m-0 lg:h-[calc(100vh-13rem)] lg:overflow-hidden">
          {mapOpen && (
            <div className="lg:col-span-8 bg-card min-h-[420px] lg:h-full lg:min-h-0 relative">
              <Button size="sm" variant="outline" onClick={() => setMapOpen(false)} className="absolute top-3 right-3 z-[500] h-8 px-2 font-mono uppercase text-[10px] bg-card/95 backdrop-blur border-border" data-testid="hide-map-btn">
                <ChevronLeft className="w-3 h-3 mr-1"/>Hide Map
              </Button>
              <MapView center={[49.2827,-123.1207]} markers={driverMarkers} fit={driverFit} fitKey={driverFitKey} height="100%"/>
            </div>
          )}
          <div className={`${mapOpen ? "lg:col-span-4" : "lg:col-span-12"} bg-card lg:h-full lg:min-h-0 overflow-y-auto hide-scrollbar`}>
            <div className="p-4 border-b border-border sticky top-0 bg-card z-10 flex gap-2">
              {!mapOpen && (
                <Button variant="outline" onClick={() => setMapOpen(true)} className="h-12 px-3 font-mono uppercase text-[10px]" data-testid="show-map-btn">
                  <ChevronRight className="w-3 h-3 mr-1"/><MapIcon className="w-3 h-3"/>
                </Button>
              )}
              <SearchBookingsDialog accounts={accounts} deliveries={deliveries} drivers={drivers} onUpdated={load} />
              <Dialog open={newBookingOpen} onOpenChange={setNewBookingOpen}><DialogTrigger asChild>
                <Button className="flex-1 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase tracking-wider font-bold h-12" data-testid="new-delivery-btn"><Plus className="w-4 h-4 mr-2"/>New Booking</Button>
              </DialogTrigger><DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto bg-card border-2">
                <DialogHeader><DialogTitle className="font-display uppercase tracking-tighter text-2xl">Book Delivery</DialogTitle></DialogHeader>
                <CreateDeliveryForm accounts={accounts} drivers={drivers} onCreated={(d) => { setNewBookingOpen(false); load(); }}/>
              </DialogContent></Dialog>
            </div>
            <div className="divide-y divide-border">
              {reorders.length > 0 && (
                <div className="border-b-2 border-orange-500" data-testid="reorder-alert-banner">
                  <button onClick={() => setReordersOpen((o) => !o)} className="w-full flex items-center justify-between p-3 bg-orange-500/10 hover:bg-orange-500/20 transition-colors" data-testid="reorders-toggle">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-orange-500 flex items-center gap-2">
                      <AlertTriangle className="w-3 h-3" /> {reorders.length} Route Change Request{reorders.length > 1 ? "s" : ""}
                    </span>
                    {reordersOpen ? <ChevronUp className="w-4 h-4 text-orange-500" /> : <ChevronDown className="w-4 h-4 text-orange-500" />}
                  </button>
                  {reordersOpen && (
                    <div className="px-3 pb-3 bg-orange-500/10">
                      {reorders.map((r) => (
                        <button key={r.id} onClick={() => setReorderDialog(r)} className="w-full text-left p-2 mb-1 border border-orange-500/50 hover:border-orange-500 bg-muted/70" data-testid={`reorder-item-${r.id}`}>
                          <div className="font-display uppercase text-sm">{r.driver_name}</div>
                          <div className="font-mono text-[10px] text-muted-foreground">{r.status === "pending" ? "AWAITING APPROVAL (critical priority)" : "AUTO-APPLIED · review"}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div>
                <div className="bg-muted/40">
                  <button onClick={() => setBookingsOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 px-3 pt-3 pb-2 hover:bg-muted/70 transition-colors" data-testid="bookings-toggle">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {deliveries.length} Booking{deliveries.length === 1 ? "" : "s"} / Trips{statusFilter ? ` · ${visibleDeliveries.length} ${STATUS_META[statusFilter].label}` : ""}
                    </span>
                    {bookingsOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
                  </button>
                  {statusCounts.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 px-3 pb-3" data-testid="bookings-subcounts">
                      {statusFilter && (
                        <button onClick={() => setStatusFilter(null)} className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-slate-300 text-muted-foreground hover:border-foreground transition-colors" data-testid="bookings-filter-all">All ({deliveries.length})</button>
                      )}
                      {statusCounts.map(({ s, n }) => {
                        const active = statusFilter === s;
                        return (
                          <button
                            key={s}
                            onClick={() => pickStatus(s)}
                            className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border transition-colors ${active ? "bg-brand-green text-black border-brand-green font-bold" : `border-slate-300 hover:border-brand-green ${STATUS_META[s].cls}`}`}
                            data-testid={`bookings-filter-${s}`}
                          >
                            {n} {STATUS_META[s].label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {bookingsOpen && (
                  <div className="divide-y divide-border">
                    {visibleDeliveries.map(d => {
                      const ro = reorderByDelivery[d.id];
                      return (
                      <div key={d.id} onClick={() => setTripDetail(d)} className={`p-4 hover:bg-muted/30 border-2 border-transparent cursor-pointer ${ro ? "reorder-flash" : ""}`} data-testid={`delivery-${d.delivery_number}`}>
                        <div className="flex items-start justify-between mb-1">
                          <div>
                            <div className="font-mono text-xs text-brand-green font-bold tracking-widest">{d.delivery_number}</div>
                            <div className="font-mono text-[10px] text-muted-foreground">{d.account_code} · {d.service_priority}</div>
                          </div>
                          <Badge status={d.status}/>
                        </div>
                        <div className="font-mono text-[10px] text-muted-foreground space-y-0.5">
                          <div>↑ {d.pickup.address}</div>
                          <div>↓ {d.dropoff.address}</div>
                          <div className="text-brand-green">${d.pricing?.total?.toFixed(2)} · {d.weight_kg}kg · {d.pieces}pc</div>
                        </div>
                        {ro && <button onClick={(e) => { e.stopPropagation(); setReorderDialog(ro); }} className="font-mono text-[10px] uppercase text-orange-500 mt-1 flex items-center gap-1 hover:underline" data-testid={`reorder-review-${d.delivery_number}`}><AlertTriangle className="w-3 h-3" />Route change — tap to review</button>}
                        {d.driver_name ? (
                          <div className="font-mono text-[10px] uppercase text-muted-foreground mt-1">Driver: <span className="text-foreground">{d.driver_number} {d.driver_name}</span></div>
                        ) : (
                          <div className="font-mono text-[10px] uppercase text-brand-warning mt-1">Unassigned — tap to assign</div>
                        )}
                      </div>
                      );
                    })}
                    {visibleDeliveries.length===0 && <div className="p-8 text-center font-mono text-xs uppercase text-muted-foreground">{statusFilter ? `No ${STATUS_META[statusFilter].label} bookings` : "No deliveries"}</div>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="accounts" className="m-0 p-6"><AccountsPanel accounts={accounts} onChange={load}/></TabsContent>
        <TabsContent value="customers" className="m-0 p-6"><CustomersPanel customers={customers} accounts={accounts} onChange={load} magicLinkResult={magicLinkResult} setMagicLinkResult={setMagicLinkResult}/></TabsContent>
        <TabsContent value="drivers" className="m-0 p-6"><DriversPanel drivers={drivers} onChange={load}/></TabsContent>
        {isAdmin && <TabsContent value="users" className="m-0 p-6"><UsersPanel users={users} onChange={load}/></TabsContent>}
      </Tabs>

      <Dialog open={!!reorderDialog} onOpenChange={(o)=>!o && setReorderDialog(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col bg-card border-2 border-orange-500">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tighter text-2xl flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-orange-400"/>Route Change</DialogTitle></DialogHeader>
          {reorderDialog && (() => {
            const curSeq = {};
            (reorderDialog.current_order||[]).forEach((o)=>{ curSeq[o.delivery_id]=o.sequence; });
            return (
            <div className="flex flex-col gap-4 min-h-0">
              <div className="font-mono text-xs text-muted-foreground shrink-0">
                <span className="text-foreground uppercase">{reorderDialog.driver_name}</span> · {reorderDialog.status === "pending" ? "requesting approval (critical priority)" : "auto-applied — you may reject to revert"}
                {reorderDialog.reason && <div className="mt-1 text-orange-400">Reason: {reorderDialog.reason}</div>}
              </div>
              <div className="grid grid-cols-2 gap-3 min-h-0">
                <div className="flex flex-col min-h-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 shrink-0">Current order</div>
                  <div className="space-y-1 overflow-y-auto max-h-64 pr-1 hide-scrollbar">{(reorderDialog.current_order||[]).map((o)=>(
                    <div key={o.delivery_id} className="border border-border p-2">
                      <div className="font-mono text-[10px] text-brand-green">{o.sequence}. {o.delivery_number}</div>
                      <div className="font-mono text-[9px] text-muted-foreground">{o.service_priority} · {o.dropoff}</div>
                    </div>))}</div>
                </div>
                <div className="flex flex-col min-h-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-orange-400 mb-2 flex items-center gap-1 shrink-0"><ArrowRight className="w-3 h-3"/>Proposed</div>
                  <div className="space-y-1 overflow-y-auto max-h-64 pr-1 hide-scrollbar">{(reorderDialog.proposed_order||[]).map((o)=>{
                    const moved = curSeq[o.delivery_id] !== undefined && curSeq[o.delivery_id] !== o.sequence;
                    return (
                    <div key={o.delivery_id} className={`border p-2 ${moved ? "border-brand-danger" : "border-orange-500/50"}`}>
                      <div className={`font-mono text-[10px] ${moved ? "text-brand-danger" : "text-orange-400"}`}>{o.sequence}. {o.delivery_number}</div>
                      <div className="font-mono text-[9px] text-muted-foreground">{o.service_priority} · {o.dropoff}</div>
                      {moved && <div className="font-mono text-[9px] text-brand-danger uppercase tracking-widest mt-0.5">⚠ Moved from #{curSeq[o.delivery_id]}</div>}
                    </div>);
                  })}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 shrink-0">
                <Button variant="outline" onClick={()=>resolveReorder(reorderDialog.id,"reject")} className="h-12 font-mono uppercase tracking-wider border-brand-danger text-brand-danger" data-testid="reject-reorder-btn">Reject</Button>
                <Button onClick={()=>resolveReorder(reorderDialog.id,"approve")} className="h-12 bg-brand-success text-black hover:bg-brand-success/90 font-mono uppercase tracking-wider font-bold" data-testid="approve-reorder-btn">Approve</Button>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <TripDetailsDialog trip={tripDetail} drivers={drivers} onClose={() => setTripDetail(null)} onUpdated={load} />
    </div>
  );
}

function AccountsPanel({ accounts, onChange }) {
  const [f, setF] = useState({ account_code:"", name:"", type:"corporate", billing_address:"", contact_name:"", contact_email:"", contact_phone:"", credit_terms:"Net 30" });
  const submit = async (e) => { e.preventDefault(); try { await api.post("/accounts", f); toast.success("Account created"); setF({...f,account_code:"",name:""}); onChange(); } catch(err){ toast.error(formatApiError(err)); } };
  return (<div className="grid lg:grid-cols-2 gap-6">
    <div><div className="font-display uppercase mb-3">All Accounts</div>
      <div className="border border-border divide-y divide-border">{accounts.map(a=>(
        <div key={a.id} className="p-3 flex justify-between" data-testid={`account-${a.account_code}`}>
          <div><div className="font-mono text-xs text-brand-green">{a.account_code}</div><div className="font-display uppercase text-sm">{a.name}</div></div>
          <div className="font-mono text-[10px] text-muted-foreground text-right">{a.type.toUpperCase()}<br/>{a.credit_terms}</div>
        </div>))}</div></div>
    <div><div className="font-display uppercase mb-3">New Account</div>
      <form onSubmit={submit} className="space-y-3">
        <Input required placeholder="Account code (ACME-01)" value={f.account_code} onChange={(e)=>setF({...f,account_code:e.target.value})} className={cls} data-testid="acct-code"/>
        <Input required placeholder="Company Name" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} className={cls} data-testid="acct-name"/>
        <Select value={f.type} onValueChange={(v)=>setF({...f,type:v})}><SelectTrigger className={cls}><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value="corporate">Corporate</SelectItem><SelectItem value="cash">Cash / Walk-in</SelectItem></SelectContent></Select>
        <Input placeholder="Billing address" value={f.billing_address} onChange={(e)=>setF({...f,billing_address:e.target.value})} className={cls}/>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Contact name" value={f.contact_name} onChange={(e)=>setF({...f,contact_name:e.target.value})} className={cls}/>
          <Input placeholder="Contact email" value={f.contact_email} onChange={(e)=>setF({...f,contact_email:e.target.value})} className={cls}/>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Contact phone" value={f.contact_phone} onChange={(e)=>setF({...f,contact_phone:e.target.value})} className={cls}/>
          <Input placeholder="Credit terms" value={f.credit_terms} onChange={(e)=>setF({...f,credit_terms:e.target.value})} className={cls}/>
        </div>
        <Button className="w-full h-11 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase font-bold" data-testid="submit-account">Create</Button>
      </form></div></div>);
}

function CustomersPanel({ customers, accounts, onChange, magicLinkResult, setMagicLinkResult }) {
  const [f, setF] = useState({ email:"", name:"", account_id:"" });
  const submit = async (e) => { e.preventDefault(); try { await api.post("/customers", { ...f, account_id: f.account_id||null }); toast.success("Customer added"); setF({email:"",name:"",account_id:""}); onChange(); } catch(err){ toast.error(formatApiError(err)); } };
  const sendLink = async (email) => { try { const r = await api.post("/auth/customer/request-link", { email }); setMagicLinkResult({ email, link: r.data.dev_link }); toast.success("Magic link generated"); } catch(err){ toast.error(formatApiError(err)); } };
  return (<div className="grid lg:grid-cols-2 gap-6">
    <div><div className="font-display uppercase mb-3">Customers</div>
      <div className="border border-border divide-y divide-border">{customers.map(c=>(
        <div key={c.id} className="p-3 flex justify-between items-center" data-testid={`customer-${c.email}`}>
          <div><div className="font-display uppercase text-sm">{c.name}</div><div className="font-mono text-[10px] text-muted-foreground">{c.email} · {accounts.find(a=>a.id===c.account_id)?.account_code || "—"}</div></div>
          <Button size="sm" variant="outline" onClick={()=>sendLink(c.email)} className="font-mono uppercase text-[10px]" data-testid={`link-${c.email}`}>Send Link</Button>
        </div>))}</div>
      {magicLinkResult && (<div className="mt-3 border-2 border-dashed border-brand-green p-3 font-mono text-xs bg-brand-green/5" data-testid="magic-link-banner">
        <div className="uppercase tracking-widest text-muted-foreground mb-1">MOCK EMAIL // Copy & send to {magicLinkResult.email}</div>
        <div className="break-all text-brand-green">{magicLinkResult.link}</div>
        <Button size="sm" variant="outline" onClick={()=>navigator.clipboard.writeText(magicLinkResult.link)} className="mt-2 font-mono uppercase text-[10px]"><Copy className="w-3 h-3 mr-1"/>Copy</Button>
      </div>)}
    </div>
    <div><div className="font-display uppercase mb-3">Add Customer</div>
      <form onSubmit={submit} className="space-y-3">
        <Input required placeholder="Name" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} className={cls}/>
        <Input required type="email" placeholder="Email" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} className={cls} data-testid="new-cust-email"/>
        <Select value={f.account_id} onValueChange={(v)=>setF({...f,account_id:v})}><SelectTrigger className={cls}><SelectValue placeholder="Link to account (optional)"/></SelectTrigger>
          <SelectContent>{accounts.filter(a=>a.type==="corporate").map(a=><SelectItem key={a.id} value={a.id}>{a.account_code} — {a.name}</SelectItem>)}</SelectContent></Select>
        <Button className="w-full h-11 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase font-bold" data-testid="submit-customer">Create</Button>
      </form></div></div>);
}

const emptyDriver = { driver_number:"", name:"", phone:"", dob:"", license_number:"", license_photo_url:"", vehicle_type:"", gvwr:"", payload_length:"", inside_height:"" };

function DriverFormDialog({ mode, driver, onSaved, trigger }) {
  const [f, setF] = useState(emptyDriver);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(mode === "edit"
      ? { ...emptyDriver, ...Object.fromEntries(Object.keys(emptyDriver).map(k => [k, driver?.[k] ?? ""])) }
      : emptyDriver);
  }, [open, mode, driver]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "edit") { await api.patch(`/drivers/${driver.id}`, f); toast.success("Driver updated"); }
      else { await api.post("/drivers", f); toast.success("Driver added"); }
      setOpen(false); onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const onLicensePhoto = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader(); r.onload = () => setF(v => ({ ...v, license_photo_url: r.result })); r.readAsDataURL(file);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-card border-2">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tighter text-2xl">{mode === "edit" ? "Edit Driver" : "New Driver"}</DialogTitle></DialogHeader>
        <DialogDescription className="font-mono text-[11px] text-muted-foreground -mt-2">{mode === "edit" ? "Update this driver's details. Phone is saved in international format." : "Add a new driver. They log in by phone + SMS code."}</DialogDescription>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Input required placeholder="Driver # (e.g. #12)" value={f.driver_number} onChange={(e)=>setF({...f,driver_number:e.target.value})} className={cls} data-testid="drv-number"/>
            <Input required placeholder="Full name" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} className={cls} data-testid="drv-name"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input required placeholder="Phone (+1604...)" value={f.phone} onChange={(e)=>setF({...f,phone:e.target.value})} className={cls} data-testid="drv-phone"/>
            <div className="space-y-1"><Label className="font-mono text-[10px] uppercase tracking-widest">Date of Birth</Label><Input type="date" value={f.dob} onChange={(e)=>setF({...f,dob:e.target.value})} className={cls} data-testid="drv-dob"/></div>
          </div>
          <Input placeholder="Driver's licence #" value={f.license_number} onChange={(e)=>setF({...f,license_number:e.target.value})} className={cls} data-testid="drv-license"/>
          <div>
            <label className="block border-2 border-dashed border-border p-3 text-center cursor-pointer hover:border-brand-green transition">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{f.license_photo_url ? "Licence photo added — tap to replace" : "Add photo of driver's licence (optional)"}</span>
              <input type="file" accept="image/*" onChange={onLicensePhoto} className="hidden" data-testid="drv-license-photo"/>
            </label>
            {f.license_photo_url && <img src={f.license_photo_url} alt="licence" className="mt-2 w-full max-h-32 object-contain border border-border"/>}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-brand-green pt-1">Vehicle</div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Vehicle type" value={f.vehicle_type} onChange={(e)=>setF({...f,vehicle_type:e.target.value})} className={cls} data-testid="drv-vehicle-type"/>
            <Input placeholder="GVWR" value={f.gvwr} onChange={(e)=>setF({...f,gvwr:e.target.value})} className={cls} data-testid="drv-gvwr"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Payload length" value={f.payload_length} onChange={(e)=>setF({...f,payload_length:e.target.value})} className={cls} data-testid="drv-payload"/>
            <Input placeholder="Inside height" value={f.inside_height} onChange={(e)=>setF({...f,inside_height:e.target.value})} className={cls} data-testid="drv-inside-height"/>
          </div>
          <Button disabled={busy} className="w-full h-11 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase font-bold" data-testid="submit-driver">{busy ? "Saving…" : (mode === "edit" ? "Save Changes" : "Create Driver")}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DriversPanel({ drivers, onChange }) {
  const setActive = async (d, active) => {
    try { await api.post(`/drivers/${d.id}/active`, { active }); toast.success(active?"Driver set active":"Driver set inactive"); onChange(); }
    catch(err){ toast.error(formatApiError(err)); }
  };
  const forceOffline = async (d) => {
    try { await api.post(`/drivers/${d.id}/offline`); toast.success("Driver set offline"); onChange(); }
    catch(err){ toast.error(formatApiError(err)); }
  };
  const forceOnline = async (d) => {
    try { await api.post(`/drivers/${d.id}/online`); toast.success("Driver set online"); onChange(); }
    catch(err){ toast.error(formatApiError(err)); }
  };
  const removeDriver = async (d) => {
    try { await api.delete(`/drivers/${d.id}`); toast.success("Driver deleted"); onChange(); }
    catch(err){ toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="font-display uppercase">Drivers</div>
        <DriverFormDialog mode="create" onSaved={onChange} trigger={
          <Button className="bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase text-xs font-bold" data-testid="add-driver-btn"><Plus className="w-4 h-4 mr-2"/>Add Driver</Button>
        }/>
      </div>
      <div className="border border-border divide-y divide-border">{drivers.map(d=>(
        <div key={d.id} className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`driver-row-${d.id}`}>
          <div>
            <div className="font-display uppercase text-sm">{d.driver_number} {d.name} {d.active===false && <span className="text-brand-danger normal-case">(Inactive)</span>}</div>
            <div className="font-mono text-[10px] text-muted-foreground">{d.phone} · {d.vehicle_type||d.vehicle||"—"}{d.gvwr?` · GVWR ${d.gvwr}`:""}</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`font-mono text-[10px] uppercase px-2 py-1 border ${d.online?"border-brand-success text-brand-success":"border-muted-foreground text-muted-foreground"}`}>{d.online?"ONLINE":"OFFLINE"}</span>
            <Button size="sm" variant="outline" disabled={d.online || d.active===false} onClick={()=>forceOnline(d)} className="font-mono uppercase text-[10px] h-7" data-testid={`online-btn-${d.id}`}>Set Online</Button>
            <Button size="sm" variant="outline" disabled={!d.online} onClick={()=>forceOffline(d)} className="font-mono uppercase text-[10px] h-7" data-testid={`offline-btn-${d.id}`}>Set Offline</Button>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase text-muted-foreground">{d.active===false?"Inactive":"Active"}</span>
              <Switch checked={d.active!==false} onCheckedChange={(v)=>setActive(d,v)} data-testid={`active-toggle-${d.id}`}/>
            </div>
            <DriverFormDialog mode="edit" driver={d} onSaved={onChange} trigger={
              <Button size="sm" variant="outline" className="font-mono uppercase text-[10px] h-7" data-testid={`edit-driver-btn-${d.id}`}><Pencil className="w-3 h-3 mr-1"/>Edit</Button>
            }/>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" className="font-mono uppercase text-[10px] h-7 border-brand-danger text-brand-danger hover:bg-brand-danger hover:text-white" data-testid={`delete-driver-btn-${d.id}`}><Trash2 className="w-3 h-3 mr-1"/>Delete</Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-card border-2">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display uppercase">Delete {d.driver_number} {d.name}?</AlertDialogTitle>
                  <AlertDialogDescription className="font-mono text-xs">
                    This permanently removes the driver. You can only delete a driver with no active deliveries — otherwise set them inactive instead. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid={`delete-cancel-${d.id}`}>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={()=>removeDriver(d)} className="bg-brand-danger text-white hover:bg-brand-danger/90" data-testid={`delete-confirm-${d.id}`}>Delete Driver</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>))}</div>
    </div>);
}

function UsersPanel({ users, onChange }) {
  const [f, setF] = useState({ name:"", email:"", password:"", role:"dispatcher" });
  const submit = async (e) => { e.preventDefault(); try { await api.post("/users", f); toast.success("User created"); setF({name:"",email:"",password:"",role:"dispatcher"}); onChange(); } catch(err){ toast.error(formatApiError(err)); } };
  return (<div className="grid lg:grid-cols-2 gap-6">
    <div><div className="font-display uppercase mb-3">Team</div>
      <div className="border border-border divide-y divide-border">{users.map(u=>(
        <div key={u.id} className="p-3 flex justify-between"><div><div className="font-display uppercase text-sm">{u.name}</div><div className="font-mono text-[10px] text-muted-foreground">{u.email}</div></div><span className="font-mono text-[10px] uppercase border border-brand-green text-brand-green px-2 py-1">{u.role}</span></div>))}</div></div>
    <div><div className="font-display uppercase mb-3">Create Dispatcher / Accountant</div>
      <form onSubmit={submit} className="space-y-3">
        <Input required placeholder="Name" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} className={cls}/>
        <Input required type="email" placeholder="Email" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} className={cls} data-testid="new-user-email"/>
        <Input required type="password" placeholder="Password (min 6)" value={f.password} onChange={(e)=>setF({...f,password:e.target.value})} className={cls}/>
        <Select value={f.role} onValueChange={(v)=>setF({...f,role:v})}><SelectTrigger className={cls}><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value="dispatcher">Dispatcher</SelectItem><SelectItem value="accountant">Accountant</SelectItem></SelectContent></Select>
        <Button className="w-full h-11 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase font-bold" data-testid="submit-user">Create</Button>
      </form></div></div>);
}
